import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Calendar, Eye } from "lucide-react";
import { format } from "date-fns";

export default function InvoiceDetail({ user }) {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [project, setProject] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderFormData, setReminderFormData] = useState({
    date: "",
    remarks: "",
    followup_enabled: false,
    followup_date: "",
    followup_notes: ""
  });
  const [viewingPayment, setViewingPayment] = useState(null);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoiceDetails();
      fetchReminders();
    }
  }, [invoiceId]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invoices/${invoiceId}/details`);
      setInvoice(response.data.invoice);
      setProject(response.data.project);
    } catch (error) {
      toast.error("Failed to fetch invoice details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/reminders`);
      setReminders(response.data);
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        date: reminderFormData.date,
        remarks: reminderFormData.remarks,
      };
      if (reminderFormData.followup_enabled && reminderFormData.followup_date) {
        payload.followup_date = reminderFormData.followup_date;
        payload.followup_notes = reminderFormData.followup_notes || reminderFormData.remarks;
      }
      await api.post(`/invoices/${invoiceId}/reminders`, payload);
      toast.success(payload.followup_date
        ? "Reminder added — follow-up scheduled in your workspace"
        : "Payment reminder added successfully");
      setReminderDialogOpen(false);
      resetReminderForm();
      fetchReminders();
    } catch (error) {
      toast.error("Failed to add reminder");
      console.error(error);
    }
  };

  const resetReminderForm = () => {
    setReminderFormData({
      date: "",
      remarks: "",
      followup_enabled: false,
      followup_date: "",
      followup_notes: ""
    });
  };

  const formatPaymentStatus = (status) => {
    const statusMap = {
      "unpaid": "Unpaid",
      "partial_in_progress": "Partial In Progress",
      "in_progress": "In Progress",
      "partial_paid": "Partially Paid",
      "paid": "Paid",
      "paid_with_partial_writeoff": "Paid (Partial Write-off)",
      "writeoff": "Write-off"
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      "unpaid": "bg-red-500",
      "partial_in_progress": "bg-yellow-500",
      "in_progress": "bg-blue-500",
      "partial_paid": "bg-orange-500",
      "paid": "bg-green-500",
      "paid_with_partial_writeoff": "bg-green-400",
      "writeoff": "bg-gray-500"
    };
    return colors[status] || "bg-gray-500";
  };

  // Helper to format amount with currency label
  const formatAmountWithCurrency = (amount, currency) => {
    const curr = currency || "USD";
    return `${amount?.toLocaleString() || 0} ${curr}`;
  };

  // Get currency for the invoice
  const getInvoiceCurrency = () => {
    return invoice?.currency || "USD";
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="p-6">
          <div className="text-center py-8">Loading invoice details...</div>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout user={user}>
        <div className="p-6">
          <div className="text-center py-8">Invoice not found</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full" onClick={() => navigate("/invoices")}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Invoices
            </Button>
            <div>
              <h1 className="text-md3-display-small">Invoice {invoice.invoice_number}</h1>
              <p className="text-md3-on-surface-variant">
                {project?.project_name} - {project?.client_name}
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Summary */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">Invoice Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Invoice Amount</p>
                <p className="text-md3-headline-large">{formatAmountWithCurrency(invoice.amount, getInvoiceCurrency())}</p>
                {getInvoiceCurrency() === "CAD" && invoice.usd_amount && (
                  <p className="text-xs text-gray-500">≈ {invoice.usd_amount.toLocaleString()} USD</p>
                )}
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Amount Paid</p>
                <p className="text-md3-headline-large text-green-600">{formatAmountWithCurrency(invoice.amount_paid, getInvoiceCurrency())}</p>
                {getInvoiceCurrency() === "CAD" && invoice.usd_amount_paid && (
                  <p className="text-xs text-gray-500">≈ {invoice.usd_amount_paid.toLocaleString()} USD</p>
                )}
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Payment Status</p>
                <Badge className={getStatusColor(invoice.payment_status)}>
                  {formatPaymentStatus(invoice.payment_status)}
                </Badge>
                <div className="mt-1">
                  <Badge variant={getInvoiceCurrency() === "CAD" ? "secondary" : "outline"}>
                    {getInvoiceCurrency()}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Due Date</p>
                <p className="text-md3-title-large">{format(new Date(invoice.due_date), "MMM dd, yyyy")}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Issue Date</p>
                <p className="text-md">{format(new Date(invoice.issue_date), "MMM dd, yyyy")}</p>
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Project Reference #</p>
                <p className="text-md">{invoice.project_reference_invoice_number || "-"}</p>
              </div>
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Bill To</p>
                <p className="text-md">{invoice.bill_to_name || project?.client_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment History Section */}
        {invoice.payment_entries && invoice.payment_entries.length > 0 && (
          <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
            <CardHeader>
              <CardTitle className="text-md3-title-large text-md3-on-surface ">Payment History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment Mode</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Date Received</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.payment_entries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="font-semibold">
                        {formatAmountWithCurrency(entry.amount, getInvoiceCurrency())}
                        {getInvoiceCurrency() === "CAD" && entry.usd_amount && (
                          <div className="text-xs text-gray-500">≈ {entry.usd_amount.toLocaleString()} USD</div>
                        )}
                      </TableCell>
                      <TableCell>{entry.payment_mode || "-"}</TableCell>
                      <TableCell>{entry.payment_reference || "-"}</TableCell>
                      <TableCell>
                        {entry.date_received ? format(new Date(entry.date_received), "MMM dd, yyyy") : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{entry.payment_workflow_status}</Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          data-testid={`detail-payment-view-btn-${entry.id}`}
                          className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                          onClick={() => setViewingPayment(entry)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Payment Reminders Section */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-md3-title-large text-md3-on-surface ">Payment Reminders</CardTitle>
              {(invoice.payment_status !== "paid" && invoice.payment_status !== "paid_with_partial_writeoff") && (
                <Button onClick={() => setReminderDialogOpen(true)} size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Reminder
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {reminders.length === 0 ? (
              <div className="text-center py-8 text-md3-on-surface-variant">
                {(invoice.payment_status === "paid" || invoice.payment_status === "paid_with_partial_writeoff") 
                  ? "No payment reminders were sent for this invoice."
                  : "No payment reminders sent yet. Click \"Add Reminder\" to log a reminder."
                }
              </div>
            ) : (
              <Table className="bg-md3-surface-container rounded-md3-md">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Reminder #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead className="w-40">Logged On</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reminders.map((reminder) => (
                    <TableRow key={reminder.id}>
                      <TableCell className="font-medium">
                        <Badge variant="outline">#{reminder.reminder_number}</Badge>
                      </TableCell>
                      <TableCell>{reminder.date}</TableCell>
                      <TableCell>{reminder.remarks}</TableCell>
                      <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                        {format(new Date(reminder.created_at), "MMM dd, yyyy HH:mm")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="mt-4 p-4 bg-blue-50 rounded-md3-lg">
              <p className="text-md3-body-small text-blue-800">
                <strong>Note:</strong> Payment reminders are log entries only. Once added, they cannot be edited or deleted. 
                This maintains an accurate history of all payment follow-ups.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Add Reminder Dialog */}
        <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Add Payment Reminder</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Log a payment reminder that was sent to the client. This entry will be part of the permanent history.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddReminder} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="date" className="text-md3-label-large text-gray-700 font-medium">Date Sent *</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="date"
                  type="date"
                  value={reminderFormData.date}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, date: e.target.value })}
                  required
                  max={new Date().toISOString().split('T')[0]}
                />
                <p className="text-xs text-md3-on-surface-variant">Date when the reminder was sent to the client</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="remarks" className="text-md3-label-large text-gray-700 font-medium">Remarks *</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="remarks"
                  value={reminderFormData.remarks}
                  onChange={(e) => setReminderFormData({ ...reminderFormData, remarks: e.target.value })}
                  required
                  rows={4}
                  placeholder="e.g., Called client to follow up on payment. Requested payment by end of week."
                />
                <p className="text-xs text-md3-on-surface-variant">Details about the reminder and client response</p>
              </div>

              {/* Optional follow-up */}
              <div className="rounded-md3-lg border p-3 space-y-3" data-testid="followup-block">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reminderFormData.followup_enabled}
                    onChange={(e) => setReminderFormData({ ...reminderFormData, followup_enabled: e.target.checked })}
                    className="h-4 w-4 accent-md3-primary"
                    data-testid="followup-toggle"
                  />
                  <span className="text-md3-label-large text-gray-700 font-medium">Set a follow-up for this reminder</span>
                </label>
                {reminderFormData.followup_enabled && (
                  <div className="space-y-2 pl-6">
                    <div>
                      <Label htmlFor="followup_date" className="text-sm text-gray-700">Follow-up date <span className="text-red-500">*</span></Label>
                      <Input
                        id="followup_date"
                        type="date"
                        value={reminderFormData.followup_date}
                        onChange={(e) => setReminderFormData({ ...reminderFormData, followup_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required={reminderFormData.followup_enabled}
                        data-testid="followup-date"
                        className="bg-white border-gray-300"
                      />
                      <p className="text-xs text-md3-on-surface-variant mt-1">A task will appear in your My Workspace on this date.</p>
                    </div>
                    <div>
                      <Label htmlFor="followup_notes" className="text-sm text-gray-700">Notes (optional)</Label>
                      <Textarea
                        id="followup_notes"
                        rows={2}
                        value={reminderFormData.followup_notes}
                        onChange={(e) => setReminderFormData({ ...reminderFormData, followup_notes: e.target.value })}
                        placeholder="Leave blank to reuse the reminder remarks"
                        data-testid="followup-notes"
                        className="bg-white border-gray-300"
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-md3-lg p-3">
                <p className="text-md3-body-small text-yellow-800">
                  <strong>⚠️ Important:</strong> Once added, this reminder cannot be edited or deleted. 
                  Please ensure all information is correct before submitting.
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" 
                  variant="outline" 
                  className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                  onClick={() => setReminderDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                  Add Reminder
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Details Dialog */}
        <Dialog open={!!viewingPayment} onOpenChange={(o) => { if (!o) setViewingPayment(null); }}>
          <DialogContent className="max-w-2xl bg-white" data-testid="detail-payment-details-dialog">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold">Payment Details</DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                Full record for this payment entry
              </DialogDescription>
            </DialogHeader>
            {viewingPayment && (() => {
              const p = viewingPayment;
              const cur = getInvoiceCurrency();
              const fmtDate = (d) => d ? format(new Date(d), "MMM dd, yyyy") : "—";
              const fmtDateTime = (d) => d ? format(new Date(d), "MMM dd, yyyy · hh:mm a") : "—";
              const row = (label, value) => (
                <div className="grid grid-cols-3 gap-3 py-2 border-b last:border-b-0" key={label}>
                  <span className="text-sm text-md3-on-surface-variant">{label}</span>
                  <span className="col-span-2 text-sm font-medium text-md3-on-surface">{value ?? "—"}</span>
                </div>
              );
              return (
                <div className="space-y-4">
                  <div className="rounded-md3-lg border bg-md3-surface-container p-4">
                    <p className="text-xs uppercase tracking-wide text-md3-on-surface-variant mb-1">Amount</p>
                    <p className="text-2xl font-bold text-green-600" data-testid="detail-payment-amount">
                      {formatAmountWithCurrency(p.amount, cur)}
                    </p>
                    {cur === "CAD" && p.usd_amount ? (
                      <p className="text-xs text-gray-500 mt-1">≈ {Number(p.usd_amount).toLocaleString()} USD</p>
                    ) : null}
                    <div className="mt-2 flex gap-2 items-center">
                      <Badge variant="outline" className="text-xs">{p.payment_workflow_status || "—"}</Badge>
                      {p.payment_mode && <Badge className="bg-md3-primary text-md3-on-primary text-xs">{p.payment_mode}</Badge>}
                    </div>
                  </div>

                  <div className="rounded-md3-lg border bg-white p-4">
                    <h4 className="font-semibold mb-2 text-md3-title-medium">Payment Instrument</h4>
                    {row("Mode of Payment", p.payment_mode || "—")}
                    {row("Bank Name", p.bank_name || "—")}
                    {row("Reference / Tracking #", p.payment_reference || "—")}
                  </div>

                  <div className="rounded-md3-lg border bg-white p-4">
                    <h4 className="font-semibold mb-2 text-md3-title-medium">Timeline</h4>
                    {row("Date Received", fmtDate(p.date_received))}
                    {row("Date Deposited", fmtDate(p.date_deposited))}
                    {row("Payment Cleared Date", fmtDate(p.date_cleared))}
                  </div>

                  <div className="rounded-md3-lg border bg-white p-4">
                    <h4 className="font-semibold mb-2 text-md3-title-medium">Workflow</h4>
                    {row("Workflow Status", p.payment_workflow_status || "—")}
                    {p.payment_workflow_status === "Write-off" && row("Write-off Reason", p.writeoff_reason || "—")}
                  </div>

                  {(p.created_at || p.updated_at || p.recorded_by || p.updated_by) && (
                    <div className="rounded-md3-lg border bg-white p-4">
                      <h4 className="font-semibold mb-2 text-md3-title-medium">Audit</h4>
                      {p.recorded_by && row("Recorded By", p.recorded_by)}
                      {p.created_at && row("Created At", fmtDateTime(p.created_at))}
                      {p.updated_by && row("Updated By", p.updated_by)}
                      {p.updated_at && row("Updated At", fmtDateTime(p.updated_at))}
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="border-md3-outline hover:bg-md3-surface-container-high rounded-md3-full"
                      onClick={() => setViewingPayment(null)}
                      data-testid="detail-payment-details-close-btn"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
